import React from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useMediaQuery,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import theme from "./Theme";

interface ChristmasPromoCardProps {
  onClose?: () => void;
}

const ChristmasPromoCard: React.FC<ChristmasPromoCardProps> = ({
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleEnrollClick = () => {
    const currentLanguage = i18n.language || "en";
    const enrollPath = currentLanguage === "en" ? "/enroll" : `/${currentLanguage}/enroll`;
    history.push(enrollPath);
    if (onClose) {
      onClose();
    }
  };

  return (
    <Paper
      elevation={12}
      sx={{
        background: "#FFFFFF",
        position: "relative",
        overflow: "hidden",
        width: isMobile ? "90vw" : "500px",
        borderRadius: "20px",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
      }}
    >
      {/* Decorative Elements */}
      {!isMobile && (
        <Box
          component="img"
          src="/bird.svg"
          alt="Bird"
          sx={{
            position: "absolute",
            bottom: -30,
            right: -30,
            width: "180px",
            height: "auto",
            opacity: 0.04,
            zIndex: 0,
            rotate: "15deg",
          }}
        />
      )}

      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 3,
          width: 32,
          height: 32,
          color: "#666",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            color: "#000",
          },
        }}
        aria-label="Close"
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          px: isMobile ? 2.5 : 3.5,
          py: isMobile ? 3 : 4,
          textAlign: "center",
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src="/logo.webp"
          alt="Young Sparrows"
          sx={{
            height: isMobile ? "42px" : "48px",
            width: "auto",
            mb: 3,
            opacity: 0.95,
          }}
        />

        {/* Title */}
        <Typography
          sx={{
            fontFamily: "Open Sans Bold",
            color: "#224A85",
            fontSize: isMobile ? "1.15rem" : "1.35rem",
            mb: 2.5,
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
          }}
        >
          {t("ChristmasPromo.title")}
        </Typography>

        {/* Discount Badge */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "baseline",
            gap: 1,
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Open Sans Extra Bold",
              fontSize: isMobile ? "3rem" : "3.5rem",
              color: "#F89C27",
              lineHeight: 1,
              letterSpacing: "-0.03em",
            }}
          >
            20%
          </Typography>
          <Typography
            sx={{
              fontFamily: "Open Sans",
              fontSize: isMobile ? "0.9rem" : "1rem",
              color: "#666",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t("ChristmasPromo.off")}
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          sx={{
            color: "#555",
            fontSize: isMobile ? "0.85rem" : "0.9rem",
            lineHeight: 1.6,
            mb: 2.5,
            mx: "auto",
          }}
        >
          {t("ChristmasPromo.description")}
        </Typography>

        {/* Benefits */}
        <Box
          sx={{
            textAlign: "left",
            mx: "auto",
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              color: "#555",
              fontSize: isMobile ? "0.8rem" : "0.85rem",
              lineHeight: 1.6,
              mb: 0.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <span style={{ color: "#4CAF50", flexShrink: 0 }}>✔</span>
            <span>{t("ChristmasPromo.benefit1")}</span>
          </Typography>
          <Typography
            sx={{
              color: "#555",
              fontSize: isMobile ? "0.8rem" : "0.85rem",
              lineHeight: 1.6,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <span style={{ color: "#4CAF50", flexShrink: 0 }}>✔</span>
            <span>{t("ChristmasPromo.benefit2")}</span>
          </Typography>
        </Box>

        {/* CTA */}
        <Typography
          sx={{
            color: "#224A85",
            fontFamily: "Open Sans Bold",
            fontSize: isMobile ? "0.85rem" : "0.9rem",
            mb: 3,
            lineHeight: 1.5,
            maxWidth: "340px",
            mx: "auto",
          }}
        >
          {t("ChristmasPromo.cta")}
        </Typography>

        {/* CTA Button */}
        <Button
          variant="contained"
          onClick={handleEnrollClick}
          fullWidth
          sx={{
            backgroundColor: "#224A85",
            color: "#FFFFFF",
            fontFamily: "Open Sans Bold",
            fontSize: isMobile ? "0.95rem" : "1rem",
            px: 4,
            py: 1.75,
            borderRadius: "10px",
            textTransform: "none",
            letterSpacing: "0.02em",
            boxShadow: "0 4px 14px rgba(34, 74, 133, 0.25)",
            transition: "all 0.25s ease",
            "&:hover": {
              backgroundColor: "#1a3a6b",
              boxShadow: "0 6px 20px rgba(34, 74, 133, 0.35)",
              transform: "translateY(-1px)",
            },
          }}
        >
          {t("ChristmasPromo.buttonText")}
        </Button>
      </Box>
    </Paper>
  );
};

export default ChristmasPromoCard;
